import React, { useEffect, useMemo } from "react";
import "./ContentStyles.css";
import {
  FaCheckCircle,
  FaEnvelope,
  FaGraduationCap,
  FaInfoCircle,
  FaShieldAlt,
  FaWhatsapp,
} from "react-icons/fa";

import type { CourseDetail, CourseLevelKey } from "./CoursesDetails";

type ContentProps = {
  courseLevel: CourseLevelKey;
  course: CourseDetail;
  studentName?: string;
  onLogout?: () => void;
};

const SUPPORT_EMAIL = "kugathaj@gmail.com";
const FALLBACK_WHATSAPP_DISPLAY = "+49 172 5467119";
const FALLBACK_WHATSAPP_TEL = "+491725467119";

const normalizePhoneForWhatsapp = (number?: string | null): string => {
  const cleanNumber = String(number || FALLBACK_WHATSAPP_TEL).replace(/\D/g, "");
  return cleanNumber || FALLBACK_WHATSAPP_TEL.replace(/\D/g, "");
};

const formatDisplayPhone = (number?: string | null): string => {
  const cleanNumber = String(number || "").trim();
  return cleanNumber || FALLBACK_WHATSAPP_DISPLAY;
};

const Content: React.FC<ContentProps> = ({ courseLevel, course, studentName }) => {
  useEffect(() => {
    document.documentElement.classList.add("content-screen-page");
    document.body.classList.add("content-screen-page");

    return () => {
      document.documentElement.classList.remove("content-screen-page");
      document.body.classList.remove("content-screen-page");
    };
  }, []);

  const whatsappNumber = useMemo(() => {
    return normalizePhoneForWhatsapp(course.whatsappNumber);
  }, [course.whatsappNumber]);

  const whatsappDisplay = useMemo(() => {
    return formatDisplayPhone(course.whatsappNumber);
  }, [course.whatsappNumber]);

  const whatsappMessage = useMemo(() => {
    return encodeURIComponent(
      `Hallo, ich interessiere mich für den ${courseLevel} Deutschkurs. Bitte senden Sie mir die Kursdetails, den Stundenplan und die Zahlungsinformationen.`,
    );
  }, [courseLevel]);

  const whatsappHref = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const mailSubject = encodeURIComponent(`DEUTSCH A1-C2 ${courseLevel} Kurs`);

  const mailBody = encodeURIComponent(
    `Hallo,\n\nich interessiere mich für den ${courseLevel} Deutschkurs.\n\nName: ${
      studentName || ""
    }\nE-Mail: \nKursniveau: ${courseLevel}\nZahlungsdetails / Zahlungsbestätigung: \n\nBitte senden Sie mir die Klassendetails und den Stundenplan.\n\nVielen Dank.`,
  );

  return (
    <main className="content-page">
      <section className="content-container">
        <header className="content-header-card">
          <div className="content-header-content">
            <p className="content-kicker">
              <FaShieldAlt />
              <span>Online Deutschkurs</span>
            </p>

            <h1 className="content-page-title">{courseLevel} Deutschkurs</h1>

            <p className="content-subtitle">
              Unsere Kurse finden online statt. Bitte kontaktieren Sie uns per
              E-Mail oder WhatsApp. Senden Sie uns Ihr gewünschtes Kursniveau,
              Ihre E-Mail-Adresse und Ihre Zahlungsdetails oder
              Zahlungsbestätigung. Danach melden wir uns mit den Klassendetails
              und dem Stundenplan.
            </p>
          </div>
        </header>

        <div className="content-learning-grid">
          <article className="content-group content-direct-group">
            <div className="content-card-particles" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>

            <section className="content-card-left">
              <div className="content-level-block">
                <span className="content-level-icon">
                  <FaGraduationCap />
                </span>

                <span className="content-status">Kurs</span>
              </div>

              <div className="content-title-block">
                <h2>{courseLevel}</h2>
                <p>Online</p>
                <small>Deutsch lernen</small>
              </div>
            </section>

            <section className="content-card-right">
              <div className="content-info-box">
                <div className="content-info-icon">
                  <FaInfoCircle />
                </div>

                <div>
                  <strong>So starten Sie den Kurs</strong>
                  <p>
                    Kontaktieren Sie uns zuerst per E-Mail oder WhatsApp. Danach
                    prüfen wir Ihre Anfrage und senden Ihnen die passenden
                    Kursdetails, den Stundenplan und alle weiteren Informationen.
                  </p>
                </div>
              </div>

              <ul className="content-step-list">
                <li>
                  <FaCheckCircle />
                  <span>Kursniveau auswählen: {courseLevel}</span>
                </li>

                <li>
                  <FaCheckCircle />
                  <span>Per E-Mail oder WhatsApp kontaktieren</span>
                </li>

                <li>
                  <FaCheckCircle />
                  <span>Zahlungsdetails oder Zahlungsbestätigung senden</span>
                </li>

                <li>
                  <FaCheckCircle />
                  <span>Klassendetails und Stundenplan erhalten</span>
                </li>
              </ul>

              <div className="content-contact-details">
                <div>
                  <span>E-Mail</span>
                  <strong>{SUPPORT_EMAIL}</strong>
                </div>

                <div>
                  <span>WhatsApp</span>
                  <strong>{whatsappDisplay}</strong>
                </div>
              </div>

              <div className="content-contact-actions">
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=${mailSubject}&body=${mailBody}`}
                  className="content-primary-action"
                >
                  <FaEnvelope />
                  <span>E-Mail senden</span>
                </a>

                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="content-secondary-action"
                >
                  <FaWhatsapp />
                  <span>WhatsApp öffnen</span>
                </a>
              </div>
            </section>
          </article>
        </div>
      </section>
    </main>
  );
};

export default Content;